import { computePosition, autoUpdate } from '@floating-ui/dom';
import { pointerDown, mediaQueries } from '../env';

// DOM
const navItemClass = 'jg-header__menu-item';
const activeBtnClass = 'is-active';
const activeNavItemClass = 'jg-header__menu-item--active';
const sections = document.querySelectorAll('.js-jg-anchor-menu');
const navItems = document.querySelectorAll(`.${navItemClass}`);

const navPopover = document.querySelector('.js-jg-nav-popover');
const togglePopoverBtn = document.querySelector('.js-jg-nav-popover-toggle');
const popoverAnchor = document.querySelector('.js-jg-nav-popover-anchor');

const navAnchors = document.querySelectorAll('.js-jg-nav-anchor');
const sectionAnimationClass = 'jg-animation-blink';
const sectionAnimationTargetClass = 'js-jg-anchor-animation-target';

// Environment
const popoverSupported = typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype === 'object' && 'popover' in HTMLElement.prototype;
const anchorSupported = "anchorName" in document.documentElement.style;

// Watchers
// Mobile menu breakpoint
const mediaQuery = window.matchMedia(mediaQueries.mediumUp);

// Config
const options = {
    root: null, // Use the viewport as the root
    rootMargin: '0px',
    // threshold: 0.15 // Trigger when matching percentage of the section is visible
    // More granular thresholds
    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
};

// State
let hasDom = false;
let hasSectionsDom = false;
let hasPopoverDom = false;
let sectionObserver = null;
let popoverPositionCleanup = null;
let scrollAdjustmentTimeout = null;

// https://claude.ai/chat/3da593d2-3134-4b59-ac16-33a9ce038f10

// Store previous observer entries and active section data
let lastY = window.scrollY;
let activeSection = null;
// Store current intersection data for all sections
let activeSectionEntries = new Map();
// Store section indices for faster lookups
let sectionIndexes = new Map();

function markActiveSection(entries) {
    // Detect scroll direction from the last known position
    const currentY = window.scrollY;
    const scrollDirection = currentY > lastY ? 'down' : 'up';
    lastY = currentY;
    
    // Update entries in our map
    entries.forEach(entry => {
        activeSectionEntries.set(entry.target.id, {
            isIntersecting: entry.isIntersecting,
            intersectionRatio: entry.intersectionRatio,
            target: entry.target
        });
    });
    
    // Get all currently intersecting entries from our map
    const intersectingEntries = Array.from(activeSectionEntries.values())
        .filter(entry => entry.isIntersecting);
    
    // If no entries are intersecting, don't change anything
    if (intersectingEntries.length === 0) return;

    // Find the entry with the largest intersection ratio
    let maxEntry = intersectingEntries[0];
    intersectingEntries.forEach(entry => {
        if (entry.intersectionRatio > maxEntry.intersectionRatio) {
            maxEntry = entry;
        }
    });
    
    // Get the current active section ID
    const currentActiveItemEl = document.querySelector(`.${activeNavItemClass}`);
    const currentActiveId = currentActiveItemEl ? 
        currentActiveItemEl.getAttribute('data-jg-target-menu') : null;
    
    // Get the potential new active section ID
    const newActiveId = maxEntry.target.id;
    
    // Determine whether to update the active section based on scroll direction
    let shouldUpdate = true;
    
    if (currentActiveId && currentActiveId !== newActiveId) {
        const currentSectionIndex = sectionIndexes.get(currentActiveId);
        const newSectionIndex = sectionIndexes.get(newActiveId);
        
        // Only update if scrolling direction matches the section order
        // When scrolling down, only activate sections below the current one
        // When scrolling up, only activate sections above the current one
        if (scrollDirection === 'down' && newSectionIndex < currentSectionIndex) {
            shouldUpdate = false;
        } else if (scrollDirection === 'up' && newSectionIndex > currentSectionIndex) {
            shouldUpdate = false;
        }
    }
    
    if (shouldUpdate) {
        // Remove active class from all nav items
        navItems.forEach(item => item.classList.remove(activeNavItemClass));
        
        // Find the corresponding nav item and add active class
        const correspondingNavItem = document.querySelector(`.${navItemClass}[data-jg-target-menu="${newActiveId}"]`);
        if (correspondingNavItem) {
            correspondingNavItem.classList.add(activeNavItemClass);
            activeSection = newActiveId;
        }
    }
}

function observeSections() {
    // Initialize section indices for faster lookup
    sections.forEach((section, index) => {
        sectionIndexes.set(section.id, index);
    });
    
    // Initialize activeSectionEntries map with all sections
    sections.forEach(section => {
        activeSectionEntries.set(section.id, {
            isIntersecting: false,
            intersectionRatio: 0,
            target: section
        });
    });
    
    sectionObserver = new IntersectionObserver(markActiveSection, options);
    // Observe all sections
    sections.forEach(section => {
        sectionObserver.observe(section);
    });
}

function unobserveSections() {
    if (sectionObserver) {
        activeSection = null;
        activeSectionEntries.clear();
        sectionIndexes.clear();
        sectionObserver.disconnect();
        sectionObserver = null;
        // Remove all active classes when destroying the observer
        navItems.forEach(item => item.classList.remove(activeNavItemClass));
    }
}

function handleMediaQueryChange(event) {
    if (event.matches) {
        upgradeDesktop();
    } else {
        downgradeDesktop();
    }
}

function watchMediaQuery() {
    mediaQuery.addEventListener('change', handleMediaQueryChange);
}

function isPopoverMounted() {
    return Boolean(navPopover.popover);
}

function isPopoverOpen() {
    return navPopover.matches(':popover-open');
}

function updatePopoverPosition() {
    return computePosition(popoverAnchor, navPopover, {
        placement: 'bottom-start',
        strategy: 'fixed'
    }).then(({ x, y }) => {
        Object.assign(navPopover.style, {
            left: `${x}px`,
            top: `${y}px`
        });
    });
}

function startPopoverPosition() {
    if (!anchorSupported) {
        updatePopoverPosition().then(() => {
            // navPopover.style.opacity = '1';
            navPopover.classList.remove('jg-is-initial');
            popoverPositionCleanup = autoUpdate(popoverAnchor, navPopover, updatePopoverPosition);
        });
    }
}

function endPopoverPosition() {
    if (!anchorSupported) {
        // navPopover.style.opacity = '0';
        navPopover.classList.add('jg-is-initial');
        if (popoverPositionCleanup) {
            popoverPositionCleanup();
        }
    }
}

function openPopover() {
    togglePopoverBtn.classList.add(activeBtnClass);
    startPopoverPosition();
}

function closePopover() {
    togglePopoverBtn.classList.remove(activeBtnClass);
    endPopoverPosition();
}

function handlePopoverToggle(event) {
    if (event.newState === 'open') {
        openPopover();
    } else {
        closePopover();
    }  
}

function handlePopoverClick(event) {
    if (event.target.classList.contains('jg-header__submenu-button')) {
        // Do not use closePopover(); directly to avoid firing it twice
        navPopover.togglePopover();
    }
}

// Workaround for Safari iOS not light dismissing popovers
// https://bugs.webkit.org/show_bug.cgi?id=267688
// https://x.com/luke_warlow/status/1781390980240835026
function emptyListener() {}

function mountPopover() {
    navPopover.popover = 'auto';
    togglePopoverBtn.popoverTargetElement = navPopover;
    togglePopoverBtn.popoverTargetAction = 'toggle';

    navPopover.addEventListener('click', handlePopoverClick);
    navPopover.addEventListener('toggle', handlePopoverToggle);
    document.body.addEventListener('pointerdown', emptyListener);
}

function unmountPopover() {    
    if ( ! isPopoverMounted()) return;

    if (isPopoverOpen()) {
        closePopover();
    }

    // Added for oddbird/popover-polyfill
    navPopover.togglePopover(false);

    navPopover.popover = null;
    togglePopoverBtn.popoverTargetElement = null;
    togglePopoverBtn.popoverTargetAction = null;

    navPopover.removeEventListener('click', handlePopoverClick);
    navPopover.removeEventListener('toggle', handlePopoverToggle);
    document.body.removeEventListener('pointerdown', emptyListener);
}

function provideNavFeedback(section) {
    const animationTarget = section.querySelector(`.${sectionAnimationTargetClass}`);
    if (!animationTarget) return;
    
    // If the animation is already running, restart it
    if (animationTarget.classList.contains(sectionAnimationClass)) {
        animationTarget.style.animation = 'none';
        animationTarget.offsetHeight; // Trigger reflow
        animationTarget.style.animation = null;
    } else {
        // If it's not running, add event listener for animation end
        animationTarget.addEventListener('animationend', handleNavAnimationEnd, { once: true });
    }
    
    // Start or restart the animation
    animationTarget.classList.add(sectionAnimationClass);
}

function handleNavAnimationEnd(event) {
    event.target.classList.remove(sectionAnimationClass);
}

function handleNavClickFeedback(event) {
    const targetId = this.getAttribute('href');
    const targetSection = document.querySelector(targetId);
    
    if (!targetSection) return;

    const initialScrollPos = window.scrollY;
    
    // Use a short delay to allow the scroll to initiate
    setTimeout(() => {
        const finalScrollPos = window.scrollY;
        if (Math.abs(finalScrollPos - initialScrollPos) < 1) {
            // No significant scroll occurred, provide visual feedback
            provideNavFeedback(targetSection);
        }
    }, 100);
}

function mountNavFeedback() {
    navAnchors.forEach(link => {
        link.addEventListener('click', handleNavClickFeedback);
    });
}

function unmountNavFeedback() {
    navAnchors.forEach(link => {
        link.removeEventListener('click', handleNavClickFeedback);
    });    
}

const remToPx = (() => {
    // Quick method: Get current root font size
    const getRootFontSize = () => {
        return parseFloat(getComputedStyle(document.documentElement).fontSize);
    };
    
    // Accurate method: Convert rem to pixels using an element
    const convertWithElement = (rem) => {
        const el = document.createElement('div');
        el.style.position = 'absolute';
        el.style.width = `${rem}rem`;
        el.style.visibility = 'hidden';
        document.body.appendChild(el);
        const pxValue = el.getBoundingClientRect().width;
        document.body.removeChild(el);
        return pxValue;
    };
    
    // Choose strategy
    return (rem, useHighAccuracy = false) => {
        if (useHighAccuracy) {
            return convertWithElement(rem);
        }
        return rem * getRootFontSize();
    };
})();

// Get the scroll-margin-top value in pixels
function getScrollMarginTopPx(element, useHighAccuracy = false) {
    const scrollMarginTop = getComputedStyle(element).getPropertyValue('--jg-scroll-margin-top');
    const remValue = parseFloat(scrollMarginTop);
    return remToPx(remValue, useHighAccuracy);
}

function getCurrentScrollPosition() {
    return window.pageYOffset || document.documentElement.scrollTop;
}

function smoothScrollTo(y) {
    window.scrollTo({
        top: y,
        behavior: 'smooth'
    });
}

// https://claude.ai/chat/a8496bdc-1f4a-4b7b-88df-64c789feddba
// https://claude.ai/chat/1b81c219-0049-4275-b226-57706bb6bbbf
function handleNavClickScroll(event) {
    const targetId = this.getAttribute('href');
    const targetSection = document.querySelector(targetId);
        
    if (!targetSection) return;

    // Debounce successive clicks by clearing any pending timeout
    if (scrollAdjustmentTimeout) {
        clearTimeout(scrollAdjustmentTimeout);
    }

    // Store the target element and its scroll-margin-top in pixels
    const scrollMarginTopPx = getScrollMarginTopPx(targetSection);
    
    scrollAdjustmentTimeout = setTimeout(() => {
        const currentScrollPosition = getCurrentScrollPosition();
        const targetRect = targetSection.getBoundingClientRect();        
        const expectedScrollPosition = targetRect.top + currentScrollPosition - scrollMarginTopPx;
        
        // Check if we need to adjust the scroll position
        if (Math.abs(currentScrollPosition - expectedScrollPosition) > 1) {
            smoothScrollTo(expectedScrollPosition);
        }
    }, 1000);    
}

function mountNavScroll() {
    navAnchors.forEach(link => {
        link.addEventListener('click', handleNavClickScroll);
    });
}

function unmountNavScroll() {
    navAnchors.forEach(link => {
        link.removeEventListener('click', handleNavClickScroll);
    });    
}

function upgradeUniversal() {
    mountNavFeedback();
    mountNavScroll();    
}

function upgradeDesktop() {
    observeSections();    
    if (hasPopoverDom) {
        mountPopover();
    }
}

function downgradeDesktop() {
    unobserveSections();
    if (hasPopoverDom) {
        unmountPopover();
    }
}

function init() {
    hasSectionsDom = sections && navItems && navAnchors;
    hasPopoverDom = navPopover && togglePopoverBtn && popoverAnchor;
    hasDom = hasSectionsDom || hasPopoverDom;
    if ( ! hasDom) return;

    upgradeUniversal();

    watchMediaQuery();
    // Set up for initial breakpoint
    handleMediaQueryChange(mediaQuery);
}

export default {
    init
}
